import React, { Children, ReactElement, ReactNode, useCallback } from 'react';
import Drawer from '@material-ui/core/Drawer';
import styled from 'styled-components';
import { SystemIconButton } from '../../../Icon/SystemIconButton';
import { SystemIcon } from '../../../Icon/SystemIcon';
import { Typography } from '../../../Typography/Typography';
import { useDataTableFilterContext } from '../../utils/DataTableFilterContext';
import { QueryModel } from '../../utils/DataTableQueryProvider/DataTableQuery.types';
import { useIntlUds } from '../../../../translations/UdsIntlProvider';
import { useEffectOnce } from '../../../../hooks';
import { useDataTableInitialSearchModel } from '../../utils/DataTableQueryProvider/DataTableInitialSearchModelProvider';
import { useDataTableQueryContext } from '../../utils/DataTableQueryProvider/DataTableQueryProvider';
import { BasePlugin } from '../../plugins';
import { DataTableFilterProps } from './DataTableFiltersChip';
import { DataTableFiltersContent } from './DataTableFiltersContent';
import { DataTableActiveQuickReportSummary } from './DataTableActiveQuickReportSummary';
import { ActiveAndAvailableFilters } from './ActiveAndAvailableFilters';

type KeyOfQuery<Q> = keyof Q & string;

interface DataTableFiltersProps<Q extends QueryModel | undefined> {
  // title?: string | JSX.Element;
  subTitle?: string | JSX.Element;
  actions?: JSX.Element;
  maxWidth?: number | string;
  minWidth?: number | string;
  /* @default: false. If you want to display all filters and remove filter selector, please use this prop */
  showAllFilters?: boolean;
  children: (props: {
    onQueryChange: (queryModel: Partial<Q> | undefined | null) => void;
  }) =>
    | ReactElement<DataTableFilterProps<KeyOfQuery<Q>>>[]
    | ReactElement<DataTableFilterProps<KeyOfQuery<Q>>>
    | ReactNode[]
    | null;
}

// eslint-disable-next-line react/jsx-no-useless-fragment
export const DataTableFilter = <Q extends string>({ children }: DataTableFilterProps<Q>) => <>{children}</>;

export const DataTableFilters = <Q extends QueryModel | undefined>({
  subTitle,
  maxWidth = '455px',
  minWidth = '455px',
  showAllFilters = true,
  children
}: DataTableFiltersProps<Q>): ReactElement | null => {
  const { intl } = useIntlUds();
  const { setFilterIds } = useDataTableInitialSearchModel();
  const { applyQuery } = useDataTableQueryContext();

  const { setVisibleFilters, isFilterDrawerOpen, setOpenFilterDrawer, setFilterDefinitions } =
    useDataTableFilterContext();

  useEffectOnce(() => {
    if (showAllFilters) {
      setVisibleFilters('all');
    }
  });

  const handleQueryChange = useCallback(
    (partialState: Partial<Q> | undefined | null) => {
      if (!partialState) {
        return;
      }
      applyQuery(partialState, { mode: 'patch', persist: true });
    },
    [applyQuery]
  );

  const nextChildren = children({ onQueryChange: handleQueryChange });

  const filters = Children.toArray(nextChildren)
    .filter((element) => !!element)
    .map((element) => {
      const el = element as { props: DataTableFilterProps<KeyOfQuery<Q>> };
      return el?.props;
    })
    .filter(Boolean);

  useEffectOnce(() => {
    // feed initialSearchModel with filter ids to remove invalid keys from a query model
    setFilterIds(filters.map((it) => it.id));
    // feed filterContext with filter ids and labels
    setFilterDefinitions(filters.map((it) => ({ id: it?.id, label: it?.label })));
  });

  return (
    <BasePlugin
      id="filters"
      key="filters"
      filters={{
        Component: (
          <Drawer
            key="filters"
            data-test="data-table-filters-drawer"
            open={isFilterDrawerOpen}
            anchor="right"
            variant="temporary"
            disableEnforceFocus
            onClose={() => setOpenFilterDrawer(false)}
          >
            <StyledPanel style={{ maxWidth, minWidth }}>
              <StyledHeader>
                <SystemIcon name="filter_list" />
                <Typography variant="h3" className="drawer-title">
                  {intl.DataTable.Filters.filtersLabel}
                </Typography>
                <SystemIconButton
                  dataTest="close-filter-drawer"
                  iconName="close"
                  onClick={() => setOpenFilterDrawer(false)}
                />
                {!!subTitle && <StyledSubTitle>{subTitle}</StyledSubTitle>}
              </StyledHeader>
              <DataTableActiveQuickReportSummary onQuickReportDrawerOpen={() => setOpenFilterDrawer(false)} />
              <ActiveAndAvailableFilters filters={filters} />
              <DataTableFiltersContent filters={filters} />
            </StyledPanel>
          </Drawer>
        )
      }}
    />
  );
};

const StyledSubTitle = styled.div`
  grid-column: 1 / 4;
  grid-row: 2;
`;

const StyledHeader = styled.div`
  display: grid;
  width: 100%;
  gap: ${({ theme }) => theme.spacingSize.l};
  grid-template-columns: 24px auto 48px;
  align-items: center;
  padding-top: ${({ theme }) => theme.spacingSize.m};
  padding-bottom: ${({ theme }) => theme.spacingSize.l};
`;

const StyledPanel = styled.div`
  padding: ${({ theme }) => theme.spacingSize.xl};
  background-color: ${(props) => props.theme.colorSurface};
  color: ${(props) => props.theme.colorOnSurface};
  height: 100vh;
`;
